<template>
  <div>
    <table v-if="members.length > 0" class="table-grid member-list">
      <thead>
        <tr class="member-list__row">
          <th class="table__heading">{{ $t("labels.name") }}</th>
          <th v-if="canSeeEmails" class="table__heading">{{ $t("labels.email") }}</th>
          <th class="table__heading">
            <span v-if="canSetValidUntil && canManageMembers">
              <template v-if="memberWord === 'user'">
                {{ $t("labels.userValidUntil") }}
              </template>
              <template v-else>
                {{ $t("labels.memberValidUntil") }}
              </template>
            </span>
          </th>
          <th class="table__heading">{{ $t("labels.role") }}</th>
          <th v-if="canManageMembers" class="member-list__heading"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="member in members"
          :key="member.id"
          class="member-list__member member-list__row"
        >
          <td class="pv-3">
            <strong v-if="member.accepted">
              {{ member.name }}
            </strong>
            <span v-else>({{ $t("labels.pending") }})</span>
          </td>

          <td v-if="canSeeEmails" class="pv-3">
            {{ member.email }}
          </td>

          <td class="pv-3">
            <input-text
              v-if="
                canSetValidUntil && canManageMembers && member.roleCode != 'OWNER'
              "
              :ref="(d) => validUntilFields[member.id] = (d as any)"
              :value="toDateString(member.validUntil)"
              style="min-width: 10rem"
              type="date"
              max="2100-12-31"
              @update:value="updateMemberValidUntil(member, $event)"
            />
          </td>

          <td class="pv-3">
            <input-dropdown
              v-if="
                canManageMembers && assignableRoleCodes.includes(member.roleCode)
              "
              dropdown-class="dropdown-slim"
              item-key="roleCode"
              :value="rolesByCode[member.roleCode]"
              :options="rolesUserCanAssign"
              @update:value="updateMemberRole(member, $event)"
            >
              <template #default="{ item }">
                {{ item?.name ?? member.roleCode }}
              </template>
            </input-dropdown>
            <span v-else :title="rolesByCode[member.roleCode]?.description">
              {{ rolesByCode[member.roleCode]?.name ?? member.roleCode }}
            </span>
          </td>

          <td v-if="canManageMembers">
            <button
              v-if="member.roleCode != 'OWNER'"
              :title="$t('actions.deleteMember')"
              type="button"
              class="button-icon"
              @click="$emit('delete', member)"
            >
              <lucide-icon name="trash-2" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="message message-info">
      {{ $t("texts.noMembers") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";

import { DateTime } from "luxon";

const props = withDefaults(defineProps<{
  members?: {
    id: number;
    roleCode: string;
    email: string | null;
    name?: string;
    validUntil?: string | null;
    accepted: string | null;
  }[];
  roles?: Record<
    string | number,
    { name: string; roleCode: string; description?: string }
  > | { name: string; roleCode: string; description?: string }[];
  maxMembers: number | null;
  canManageMembers?: boolean;
  canSeeEmails?: boolean;
  userMembership?: {
    roleCode: string;
    validUntil?: string | null;
  } | null;
  canSetValidUntil?: boolean;
  assignableRoleCodes?: string[];
  memberWord?: "member" | "user";
}>(), {
  members: () => ([]),
  roles: () => ({}),
  maxMembers: Infinity,
  canManageMembers: false,
  canSeeEmails: false,
  userMembership: null,
  canSetValidUntil: false,
  assignableRoleCodes: () => ["ADMIN", "MEMBER"],
  memberWord: "member"
});

const emit = defineEmits(["delete", "update"]);

const validUntilFields: Record<number, InstanceType<typeof InputText>> = {};

const rolesByCode = computed(() => {
  const dict: Record<
    string,
    { name: string; roleCode: string; description?: string }
  > = {};
  Object.values(props.roles).forEach((role) => (dict[role.roleCode] = role));
  return dict;
});

const rolesUserCanAssign = computed(() => {
  return Object.values(props.roles).filter((r) =>
    props.assignableRoleCodes.includes(r.roleCode)
  );
});


function updateMemberRole(member: { id: number }, role: { roleCode: string }) {
  emit("update", {
    id: member.id,
    roleCode: role.roleCode
  });
}

function updateMemberValidUntil(
  member: { id: number },
  validUntilDateString: string | null
) {
  // Prevent update if an invalid value was entered.
  const input = validUntilFields[member.id];
  const field = input.inputElement;
  const validity = field?.validity;
  if (validity && !validity?.valid) {
    return;
  }

  if (validUntilDateString) {
    const year = parseInt(validUntilDateString.substring(0, 4));
    if (!year || year < 2000) return;
  }

  const dateTime = validUntilDateString
    ? toDateTimeString(validUntilDateString)
    : null;

  emit("update", {
    id: member.id,
    validUntil: dateTime
  });
}

function toDateString(datetimestring: string | null | undefined) {
  if (datetimestring == null) return undefined;

  const isoString = toDateTimeString(datetimestring);
  if (!isoString) return undefined;

  return isoString.substring(0, 10);
}

function toDateTimeString(dateString: string | null) {
  if (dateString == null) return undefined;

  const d = new Date(dateString);
  const dateTime = DateTime.fromJSDate(d);
  if (!dateTime) return undefined;

  try {
    return dateTime.toISO();
  } catch (e) {
    console.warn(e);
    return undefined;
  }
}
</script>

<style lang="scss">
.member-list {
  gap: 4px;
  max-width: 1600px;
  column-gap: 1rem;

  @supports (grid-template-columns: subgrid) {
    display: grid;
    grid-template-columns: 1fr 1fr 11rem 12rem 3rem;
  }
  thead {
    @supports (grid-template-columns: subgrid) {
      display: contents;
    }
  }
  tbody {
    @supports (grid-template-columns: subgrid) {
      display: contents;
    }
  }

  &__row {
    text-align: left;
    padding-left: 1rem;

    grid-template-columns: 1fr 1fr 11rem 12rem 3rem;

    @supports (grid-template-columns: subgrid) {
      grid-column-start: 1;
      grid-column-end: 6;
      grid-template-columns: subgrid;
    }
  }

  &__member {
    border-radius: 5px;
    background-color: var(--color-background-deep);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}
</style>
